import * as React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { CookieConsentBanner } from "./components/ui/cookie-consent-banner";
import { PushNotificationProvider } from "./components/notifications/PushNotificationProvider";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Pricing from "./pages/Pricing";
import Help from "./pages/Help";
import HowToPlanTrip from "./pages/HowToPlanTrip";
import AiVsManualTripPlanning from "./pages/AiVsManualTripPlanning";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Referral from "./pages/Referral";
import JoinPackingList from "./pages/JoinPackingList";
import AdminAirlineUpdate from "./pages/AdminAirlineUpdate";
import EmailPreview from "./pages/EmailPreview";
import EmailPreviewUpgradeConfirmation from "./pages/EmailPreviewUpgradeConfirmation";
import BoostPassPromo from "./pages/emails/BoostPassPromo";
import UpgradePromo from "./pages/emails/UpgradePromo";
import WinBackEmail from "./pages/emails/WinBackEmail";
import GenerateBlogPosts from "./pages/GenerateBlogPosts";
import GenerateCategoryImages from "./pages/GenerateCategoryImages";
import SavedArticles from "./pages/SavedArticles";
import BlogAdmin from "./pages/BlogAdmin";
import BlogAdminEditor from "./pages/BlogAdminEditor";
import BlogAnalytics from "./pages/BlogAnalytics";
import NewsletterAdmin from "./pages/NewsletterAdmin";
import AdminSetup from "./pages/AdminSetup";
import ChatAnalytics from "./pages/ChatAnalytics";
import TripManagement from "./pages/TripManagement";
import PackingListLanding from "./pages/PackingListLanding";
import VisaLanding from "./pages/VisaLanding";
import ESIMDeviceAdmin from "./pages/ESIMDeviceAdmin";
import PolicyUpdateAdmin from "./pages/PolicyUpdateAdmin";
import EmailCampaignsAdmin from "./pages/EmailCampaignsAdmin";
import Unsubscribe from "./pages/Unsubscribe";
import OAuthConsent from "./pages/OAuthConsent";
import BoostSuccess from "./pages/BoostSuccess";

const queryClient = new QueryClient();

const App = () => (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider 
        attribute="class" 
        defaultTheme="system" 
        enableSystem
        disableTransitionOnChange
      >
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <PushNotificationProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/sign-in" element={<Navigate to="/auth" replace />} />
              <Route path="/signin" element={<Navigate to="/auth" replace />} />
              <Route path="/sign-up" element={<Navigate to="/auth" replace />} />
              <Route path="/signup" element={<Navigate to="/auth" replace />} />
              <Route path="/log-in" element={<Navigate to="/auth" replace />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/boost-success" element={<BoostSuccess />} />
              <Route path="/help" element={<Help />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/how-to-plan-a-trip" element={<HowToPlanTrip />} />
              <Route path="/blog/ai-vs-manual-trip-planning" element={<AiVsManualTripPlanning />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/saved-articles" element={<SavedArticles />} />
              <Route path="/blog/admin" element={<Navigate to="/blog-admin" replace />} />
              <Route path="/blog-admin" element={<AdminRoute><BlogAdmin /></AdminRoute>} />
              <Route path="/admin-setup" element={<AdminRoute><AdminSetup /></AdminRoute>} />
              <Route path="/chat-analytics" element={<AdminRoute><ChatAnalytics /></AdminRoute>} />
              <Route path="/blog-admin/analytics" element={<AdminRoute><BlogAnalytics /></AdminRoute>} />
              <Route path="/newsletter-admin" element={<AdminRoute><NewsletterAdmin /></AdminRoute>} />
              <Route path="/blog-admin/new" element={<AdminRoute><BlogAdminEditor /></AdminRoute>} />
              <Route path="/blog-admin/edit/:id" element={<AdminRoute><BlogAdminEditor /></AdminRoute>} />
              <Route path="/generate-blog-posts" element={<AdminRoute><GenerateBlogPosts /></AdminRoute>} />
              <Route path="/generate-category-images" element={<AdminRoute><GenerateCategoryImages /></AdminRoute>} />
              <Route path="/referral" element={<Referral />} />
              <Route path="/admin/airline-update" element={<AdminRoute><AdminAirlineUpdate /></AdminRoute>} />
              <Route path="/email-preview" element={<AdminRoute><EmailPreview /></AdminRoute>} />
              <Route path="/emails/boost-pass" element={<AdminRoute><BoostPassPromo isPreview={true} /></AdminRoute>} />
              <Route path="/emails/upgrade" element={<AdminRoute><UpgradePromo isPreview={true} /></AdminRoute>} />
              <Route path="/emails/win-back" element={<AdminRoute><WinBackEmail /></AdminRoute>} />
              <Route path="/emails/upgrade-confirmation" element={<AdminRoute><EmailPreviewUpgradeConfirmation /></AdminRoute>} />
              <Route path="/admin/esim-devices" element={<AdminRoute><ESIMDeviceAdmin /></AdminRoute>} />
              <Route path="/admin/policy-update" element={<AdminRoute><PolicyUpdateAdmin /></AdminRoute>} />
              <Route path="/admin/email-campaigns" element={<AdminRoute><EmailCampaignsAdmin /></AdminRoute>} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/trip-management" element={
                <ProtectedRoute>
                  <TripManagement />
                </ProtectedRoute>
              } />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/packing-list/:destination" element={<PackingListLanding />} />
              <Route path="/visa/:country" element={<VisaLanding />} />
              <Route path="/join/:code" element={<JoinPackingList />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsentBanner />
          </BrowserRouter>
          </PushNotificationProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </React.StrictMode>
);

export default App;
